import { GzAnimatedHighlightBoxWebComponent } from './GzAnimatedHighlightBoxWebComponent';

export class GzAnimatedHighlightBoxManager {
  // stores instances of the highlight box and the update function
  private static instances: {
    box: GzAnimatedHighlightBoxWebComponent;
    updateFn: () => void;
    // stores the custom events that are used to update the highlight box position
    customEvents: string[];
  }[] = [];

  private static readonly BORDER_WIDTH_OFFSET = 3;

  /**
   * Highlights the specified items.
   * @param items - An array of selectors or elements (or null/undefined values, which are skipped)
   * @returns The created HighlightBox element or null if no elements are found.
   */
  public static highlightElements(
    items: (string | Element | null | undefined)[],
    customEvents: string[] = []
  ): GzAnimatedHighlightBoxWebComponent | null {
    // Resolve each item to an element (or multiple elements if a selector is provided)
    const elements: Element[] = [];
    items.forEach(item => {
      if (!item) return; // Skip null or undefined items
      if (typeof item === 'string') {
        elements.push(...Array.from(document.querySelectorAll(item)));
      } else if (item instanceof Element) {
        elements.push(item);
      }
    });

    if (elements.length === 0) {
      return null;
    }

    // Compute a bounding box that covers all elements
    const calculateBoundingBox = (): { minTop: number; minLeft: number; maxBottom: number; maxRight: number } => {
      let minTop = Infinity,
        minLeft = Infinity,
        maxBottom = -Infinity,
        maxRight = -Infinity;
      elements.forEach(el => {
        const rect = el.getBoundingClientRect();
        minTop = Math.min(minTop, rect.top);
        minLeft = Math.min(minLeft, rect.left);
        maxBottom = Math.max(maxBottom, rect.bottom);
        maxRight = Math.max(maxRight, rect.right);
      });
      // Expand the bounding box by OFFSET on all sides.
      return {
        minTop: minTop - GzAnimatedHighlightBoxManager.BORDER_WIDTH_OFFSET,
        minLeft: minLeft - GzAnimatedHighlightBoxManager.BORDER_WIDTH_OFFSET,
        maxBottom: maxBottom + GzAnimatedHighlightBoxManager.BORDER_WIDTH_OFFSET,
        maxRight: maxRight + GzAnimatedHighlightBoxManager.BORDER_WIDTH_OFFSET,
      };
    };

    // Create an overlay that will be positioned over the combined bounding box
    const highlightBox: GzAnimatedHighlightBoxWebComponent = document.createElement('gz-animated-highlight-box');
    const { minTop, minLeft, maxBottom, maxRight } = calculateBoundingBox();
    highlightBox.updatePosition(minTop, minLeft, maxRight - minLeft, maxBottom - minTop);

    document.body.appendChild(highlightBox);
    highlightBox.classList.add('box-in');

    const updateFn = () => {
      setTimeout(() => {
        // tslint:disable-next-line:no-shadowed-variable
        const { minTop, minLeft, maxBottom, maxRight } = calculateBoundingBox();
        highlightBox.updatePosition(minTop, minLeft, maxRight - minLeft, maxBottom - minTop);
      }, 0);
    };

    window.addEventListener('scroll', updateFn);
    window.addEventListener('resize', updateFn);
    // update the highlight box position on custom events, such as element open/close, resize, toggle etc.
    customEvents.forEach(eventName => window.addEventListener(eventName, updateFn));

    this.instances.push({ box: highlightBox, updateFn, customEvents });

    return highlightBox;
  }

  /**
   * Removes the specified highlight box from the DOM and the instances array.
   * @param highlightBox
   */
  public static removeHighlight(highlightBox: GzAnimatedHighlightBoxWebComponent): void {
    // Find the corresponding instance record.
    const index = this.instances.findIndex(inst => inst.box === highlightBox);
    if (index !== -1) {
      const { updateFn, customEvents } = this.instances[index];
      // Remove the event listeners using the stored updateFn.
      window.removeEventListener('scroll', updateFn);
      window.removeEventListener('resize', updateFn);
      customEvents.forEach(eventName => window.removeEventListener(eventName, updateFn));
      // Remove the element from the DOM.
      highlightBox.remove();
      // Remove from the instances array.
      this.instances.splice(index, 1);
    }
  }

  /**
   * Removes all highlight boxes from the DOM and clears the instances array.
   */
  public static removeAllHighlights(): void {
    // Remove each highlight box from the DOM and clear the instances array.
    while (this.instances.length > 0) {
      const instance = this.instances.pop();
      if (instance) {
        const { box, updateFn, customEvents } = instance;
        window.removeEventListener('scroll', updateFn);
        window.removeEventListener('resize', updateFn);
        customEvents.forEach(eventName => window.removeEventListener(eventName, updateFn));
        box.remove();
      }
    }
  }
}
